import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import { useBreakpoints } from '@ux/component-utilities';
import { URL } from '@ux/util';

/**
 * Add query parameters from props to the url.
 *
 * @param {string} url link.
 * @param {Object} props Properties to merge with the query parameters object.
 * @returns {string} Configured url.
 * @public
 */
export function updateQuery(url, props) {
  var urlObj = new URL(url, true);
  return urlObj.set('query', _objectSpread(_objectSpread({}, urlObj.query), props)).href;
}

/**
 * Custom hook to check for breakpoints, isMobile, and collapsedLogo props
 * and determine whether to use a desktop or mobile logo
 *
 * @param {Object} props Properties.
 * @param {boolean} [props.isMobile] breakpoint already checked by consumer.
 * @param {boolean} [props.collapsedLogo] use a mobile logo at all breakpoints.
 * @returns {boolean} true for a mobile logo case.
 * @public
 */
export function useMobile(props) {
  var _useBreakpoints = useBreakpoints('mobile', 'phablet', 'tablet', 'desktop', 'large'),
    breakpoint = _useBreakpoints.breakpoint;
  // Some Logo consumers setup breakpoints themselves and will trigger
  // corresponding re-renders of the Logo. But for those that don't, Logo
  // needs to setup breakpoints on its own. This prevents spurious re-renders
  // when consumed by an already-breakpoint-sensitive component.

  var shouldConfigureBreakpoints = typeof props.isMobile !== 'boolean' && !props.collapsedLogo;
  return shouldConfigureBreakpoints ? ['mobile', 'phablet'].includes(breakpoint) : props.isMobile || props.collapsedLogo;
}

/**
 * Convert an object with date information to a Unix epoch
 *
 * @param {Object} date Date information.
 * @param {number} date.year Year
 * @param {number} date.month Month (indexed from 0)
 * @param {number} date.day Day
 * @param {number} [date.hour] Hour
 * @returns {number} Unix epoch.
 * @public
 */
export function convertToTimestamp(_ref) {
  var year = _ref.year,
    month = _ref.month,
    day = _ref.day,
    _ref$hour = _ref.hour,
    hour = _ref$hour === void 0 ? null : _ref$hour;
  // subtract 1 from the month since Date months are indexed from 0
  return new Date(year, month - 1, day, hour).getTime();
}

/**
 * Renders a temporary logo during a specified time range.
 *
 * @param {Object} props Properties
 * @param {Object} [props.tempLogo] Object supplied if a temporary logo could apply
 * @param {string} props.tempLogo.filename Name of the logo file on the CDN
 * @param {Object} props.tempLogo.start Object defining time to start showing the temp logo
 * @param {Object} props.tempLogo.end Object defining time to stop showing the temp logo
 * @param {Object} props.tempLogo.styles Object defining CSS style attributes to apply to the logo `img` tag
 * @param {string} props.tempLogo.altText altText for the logo `img` tag
 * @param {string} props.figClass Classname for figure wrapping the logo img
 * @param {string} props.urls.cdn.href Url for the CDN containing the logo image src
 * @returns {ReactElement} temporary logo, during the designated timeframe.
 * @public
 */
export function useTemporaryLogo(props) {
  if (!props.tempLogo) return null;
  var _props$tempLogo = props.tempLogo,
    filename = _props$tempLogo.filename,
    start = _props$tempLogo.start,
    end = _props$tempLogo.end,
    _props$tempLogo$style = _props$tempLogo.styles,
    styles = _props$tempLogo$style === void 0 ? {} : _props$tempLogo$style,
    _props$tempLogo$altTe = _props$tempLogo.altText,
    altText = _props$tempLogo$altTe === void 0 ? '' : _props$tempLogo$altTe;
  var startDate = convertToTimestamp(start);
  var endDate = convertToTimestamp(end);
  var now = Date.now();
  if (startDate < now && now < endDate) {
    var cdn = "".concat(props.urls.cdn.href, "ux/sales-header/");
    return /*#__PURE__*/React.createElement("figure", {
      className: props.figClass,
      "aria-label": "GoDaddy",
      style: {
        margin: 0
      }
    }, /*#__PURE__*/React.createElement("img", {
      alt: altText,
      src: "".concat(cdn).concat(filename),
      onError: function onError(e) {
        // @ts-ignore
        /** @type {HTMLImageElement} */
        var t = e.target;
        t.onerror = null;
        // default logo fallback
        // TODO: update default logo fallback
        t.src = "".concat(cdn, "gd-logo.svg");
        t.setAttribute('style', null);
      },
      style: styles
    }));
  }
  return null;
}